import React, { useState } from 'react';
import { HiExclamationCircle, HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { InlineBar } from './StatTiles';
import { STATUS, fmt, prettyLabel, relativeTime, statusBadge } from './vizTokens';

/** Compact run counters for a single day, with the failure detail folded in. */
export const DayActivity = ({ title, data, errorSyncs }) => {
  const failures = (data?.errors ?? 0) + (data?.failed_runs ?? 0);
  // Closed by default, like the failed-documents panel: the counter above
  // already says something went wrong, and auto-expanding stack traces made
  // this card stretch the whole dashboard row on a bad day.
  const [open, setOpen] = useState(false);

  const metrics = [
    { key: 'documents_added', label: 'Docs added' },
    { key: 'new', label: 'New files' },
    { key: 'modified', label: 'Modified' },
    { key: 'deleted', label: 'Deleted' },
    { key: 'runs', label: 'Sync runs' },
    { key: 'errors', label: 'Errors' },
  ];

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-slate-800 mb-4">{title}</h3>

      <dl className="grid grid-cols-3 gap-x-4 gap-y-4">
        {metrics.map(({ key, label }) => {
          const value = data?.[key] ?? 0;
          const isProblem = key === 'errors' && value > 0;
          return (
            <div key={key}>
              <dt className="text-xs text-slate-500 mb-1">{label}</dt>
              <dd className="flex items-center gap-1.5">
                {isProblem && (
                  <HiExclamationCircle
                    className="w-4 h-4 shrink-0"
                    style={{ color: STATUS.critical }}
                    aria-hidden="true"
                  />
                )}
                <span className={`text-lg font-semibold ${value === 0 ? 'text-slate-300' : 'text-slate-900'}`}>
                  {fmt(value)}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>

      {failures > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between text-xs font-medium text-slate-600 hover:text-slate-800"
          >
            <span className="flex items-center gap-1.5">
              <HiExclamationCircle className="w-4 h-4" style={{ color: STATUS.critical }} aria-hidden="true" />
              {data.failed_runs > 0 && `${fmt(data.failed_runs)} failed run${data.failed_runs > 1 ? 's' : ''}`}
              {data.failed_runs > 0 && data.errors > 0 && ' · '}
              {data.errors > 0 && `${fmt(data.errors)} file error${data.errors > 1 ? 's' : ''}`}
            </span>
            {open ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
          </button>

          {open && (
            <div className="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
              {errorSyncs?.length ? (
                errorSyncs.map((s, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-medium text-slate-700">{prettyLabel(s.source)}</span>
                      <span className={`px-2 py-0.5 rounded-full font-medium ${statusBadge(s.status)}`}>
                        {s.status}
                      </span>
                    </div>
                    {s.raw_error && (
                      <p className="mt-1 px-2 py-1.5 bg-white rounded border border-slate-100 text-slate-600 font-mono leading-relaxed break-all">
                        {s.raw_error}
                      </p>
                    )}
                    <p className="text-slate-400 mt-1">{relativeTime(s.processed_at)}</p>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400">
                  No failure detail recorded — check Sync Status for the full log.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const RecentSyncs = ({ syncs }) => (
  <div className="card">
    <h3 className="text-base font-semibold text-slate-800 mb-4">Recent sync runs</h3>
    {syncs?.length ? (
      <div className="divide-y divide-slate-50">
        {syncs.map((s, i) => (
          <div key={i} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{prettyLabel(s.source)}</p>
              <p className="text-xs text-slate-400">
                {relativeTime(s.processed_at)}
                {s.duration_s != null && ` · ${s.duration_s}s`}
              </p>
            </div>
            <div className="flex gap-2.5 text-xs text-slate-500 shrink-0 tabular-nums">
              {s.new > 0 && <span>+{fmt(s.new)}</span>}
              {s.modified > 0 && <span>~{fmt(s.modified)}</span>}
              {s.deleted > 0 && <span>−{fmt(s.deleted)}</span>}
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${statusBadge(s.status)}`}>
              {s.status}
            </span>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-slate-400">No recent sync runs.</p>
    )}
  </div>
);

export const SiteBreakdown = ({ bySite }) => {
  const entries = Object.entries(bySite ?? {});
  if (!entries.length) return null;
  const total = entries.reduce((sum, [, c]) => sum + c, 0);

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-slate-800 mb-1">By site</h3>
      <p className="text-xs text-slate-400 mb-4">{fmt(total)} documents</p>
      <div className="space-y-3">
        {entries
          .sort(([, a], [, b]) => b - a)
          .map(([name, count]) => (
            <div key={name}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-slate-700">{name}</span>
                <span className="text-slate-500 tabular-nums">{fmt(count)}</span>
              </div>
              <InlineBar value={count} total={total} />
            </div>
          ))}
      </div>
    </div>
  );
};

/**
 * Recently failed documents — collapsed by default.
 *
 * Each entry carries a full error message, so rendering them all made this one
 * panel taller than the rest of the dashboard combined and pushed everything
 * else off screen. The count is the part worth seeing at a glance; the stack
 * traces are something you go looking for, so they stay behind a click.
 */
export const FailedDocuments = ({ docs }) => {
  const [open, setOpen] = useState(false);
  if (!docs?.length) return null;

  return (
    <div className="card">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 text-left"
      >
        <HiExclamationCircle className="w-4 h-4 shrink-0" style={{ color: STATUS.critical }} aria-hidden="true" />
        <h3 className="text-base font-semibold text-slate-800">Recently failed documents</h3>
        <span className="text-xs font-medium text-slate-500">({docs.length})</span>
        <span className="ml-auto flex items-center gap-1 text-xs font-medium text-slate-500">
          {open ? 'Hide' : 'Show'}
          {open ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {open && (
      <div className="space-y-2 mt-4 max-h-96 overflow-auto pr-1">
        {docs.map((d) => (
          <div key={d.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs">
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="font-medium text-slate-700 truncate flex-1">{d.filename}</span>
              <div className="flex gap-1 shrink-0">
                {d.document_type && (
                  <span className="px-1.5 py-0.5 rounded bg-white text-slate-500 border border-slate-100">
                    {prettyLabel(d.document_type)}
                  </span>
                )}
                {d.source_type && (
                  <span className="px-1.5 py-0.5 rounded bg-white text-slate-500 border border-slate-100">
                    {prettyLabel(d.source_type)}
                  </span>
                )}
              </div>
            </div>
            {d.error_message ? (
              <p className="px-2 py-1.5 bg-white rounded border border-slate-100 text-slate-600 font-mono leading-relaxed break-all">
                {d.error_message}
              </p>
            ) : (
              <p className="text-slate-400 italic">No error message recorded.</p>
            )}
            <p className="text-slate-400 mt-1">{relativeTime(d.updated_at)}</p>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};
